import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';

import WorksheetEditor, { WorksheetEditorFormData, WorksheetModification } from '@components/forms/WorksheetEditor';
import WorksheetForm from '@components/forms/WorksheetForm';
import HandleQuery from '@components/HandleQuery';
import Modal from '@components/Modal';
import ExpertJourneyPage from '@components/pageTypes/ExpertJourneyPage';
import useCreateFileResource from '@hooks/mutations/useCreateFileResource';
import useOpener from '@hooks/useOpener';
import {
  GetJourneyWithDocumentsDocument,
  GetJourneyWithDocumentsQuery,
  GetJourneyWithDocumentsQueryVariables,
  GetWorksheetDocument,
  GetWorksheetQuery,
  GetWorksheetQueryVariables,
  WorksheetUpdateDocument,
  WorksheetUpdateMutation,
  WorksheetUpdateMutationVariables
} from '@typing/Generated';
import { ErrorSetter, handleMutationResponse } from '@utils/formUtils';

type Props = {
  journeyId: string;
  worksheetId: string;
};

const ExpertJourneyWorksheetPage = ({ journeyId, worksheetId }: Props) => {
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const [getWorksheet, getWorksheetQuery] = useLazyQuery<GetWorksheetQuery, GetWorksheetQueryVariables>(
    GetWorksheetDocument
  );

  const intl = useIntl();

  const getJourneyQuery = useQuery<GetJourneyWithDocumentsQuery, GetJourneyWithDocumentsQueryVariables>(
    GetJourneyWithDocumentsDocument,
    {
      variables: { id: journeyId }
    }
  );

  useEffect(() => {
    if (!getJourneyQuery.data?.journey) return;

    const worksheet = getJourneyQuery.data?.journey.worksheets.find(
      worksheet => worksheet.id === worksheetId || worksheet.previousIds.includes(worksheetId)
    );

    if (worksheet) {
      getWorksheet({ variables: { id: worksheet.id } });
    }
  }, [getJourneyQuery.data?.journey, getWorksheet, worksheetId]);

  const worksheetEditOpener = useOpener();
  const createFileResource = useCreateFileResource(journeyId);

  const [updateWorksheet] = useMutation<WorksheetUpdateMutation, WorksheetUpdateMutationVariables>(
    WorksheetUpdateDocument
  );

  const onSave = useCallback(
    (
      modifications: WorksheetModification[],
      setBaseErrors: (errors: string[]) => void,
      setError: ErrorSetter<WorksheetEditorFormData>,
      leavingLocation?: string
    ) =>
      updateWorksheet({
        variables: {
          id: worksheetId,
          worksheetModifications: modifications.map(modification => ({
            completed: modification.completed,
            details: modification.details,
            itemId: modification.item.id
          }))
        }
      }).then(response => {
        handleMutationResponse<WorksheetEditorFormData>({
          data: response.data?.worksheetUpdate,
          intl,
          onError: () => {
            setLoading(false);
          },
          onSuccess: () => {
            const worksheet = response.data?.worksheetUpdate?.worksheet;
            if (worksheet && !worksheet.fileResource) {
              return createFileResource({
                variables: {
                  journeyId,
                  title: worksheet.title,
                  worksheetId: worksheet.id
                }
              }).then(data => {
                if (data.data?.fileResourceCreate?.fileResource?.title) {
                  setLoading(false);
                  if (leavingLocation) {
                    setTimeout(() => history.push(leavingLocation), 300);
                  }
                }
              });
            } else {
              if (leavingLocation) {
                setTimeout(() => history.push(leavingLocation), 300);
              }
              setLoading(false);
            }
          },
          setBaseErrors,
          setError,
          txBase: 'errors.worksheet'
        });
      }),
    [createFileResource, history, intl, journeyId, updateWorksheet, worksheetId]
  );

  return (
    <ExpertJourneyPage helmetKey="pageTitles.expertJourney.worksheet.show" journeyId={journeyId}>
      <HandleQuery query={getJourneyQuery}>
        {journeyData => (
          <HandleQuery query={getWorksheetQuery}>
            {data => (
              <>
                <WorksheetEditor
                  handleWorksheetEdit={worksheetEditOpener.open}
                  initialWorksheet={data.worksheet}
                  journeyId={journeyId}
                  loading={loading}
                  onSave={onSave}
                />

                <Modal opener={worksheetEditOpener} title={intl.formatMessage({ id: 'models.worksheet.edit' })}>
                  {({ modalDisplayed }) => (
                    <WorksheetForm
                      autofocus={modalDisplayed}
                      journey={journeyData.journey}
                      onCancel={worksheetEditOpener.close}
                      onSuccess={worksheetEditOpener.close}
                      worksheet={data.worksheet}
                    />
                  )}
                </Modal>
              </>
            )}
          </HandleQuery>
        )}
      </HandleQuery>
    </ExpertJourneyPage>
  );
};

export default ExpertJourneyWorksheetPage;
